import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { appStatusMap, Status } from '../constants/AppStatuses';
import { Employee } from '../models/generated/employee';
import { Unit } from '../models/User';
import { CustomHttpError } from '../utils/CustomHttpError';
import { EnvironmentService } from './environment.service';
import { StatusService } from './status.service';
import { ToastService } from './toast.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly env: EnvironmentService = inject(EnvironmentService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly statusService: StatusService = inject(StatusService);
  private readonly userService: UserService = inject(UserService);

  private employeeObservable: Observable<Array<Employee>>;

  public getEmployees(): Observable<Array<Employee>> {
    if (!this.employeeObservable) {
      this.employeeObservable = this.httpClient.get<Array<Employee>>(`${this.env.getBaseRestUrl()}/employee`).pipe(
        catchError((err: CustomHttpError) => {
          if (err.httpError.status === 403) {
            console.error('Access denied error', err);
            return this.handleAccessDeniedError();
          } else {
            console.error('Error requesting employees', err);
            this.toastService.genericError(err.traceId);
            return of([]);
          }
        }),
        shareReplay()
      );
    }

    return this.employeeObservable;
  }

  private handleAccessDeniedError(): Observable<Array<Employee>> {
    return of(null).pipe(
      switchMap(() =>
        this.userService.getUnit().pipe(
          tap((unit: Unit) => {
            this.statusService.setStatus({
              ...appStatusMap[Status.ACCESS_DENIED],
              params: { unitName: unit?.name ? `<b>${unit.name}</b>` : '' }
            });
          }),
          map(() => [])
        )
      )
    );
  }
}
